/* eslint-disable */
/*!
 * dd-call-booker.js (v4 — multi-step wizard)
 *
 * Native Calendly scheduler client for /pages/welcome. 3-step wizard:
 *   1. CALENDAR  — month grid, pick a day
 *   2. TIME      — slot pills for that day, pick a time
 *   3. FORM      — name + email, confirm booking
 *
 * Steps animate left/right with --dd-ease-smooth (reduced-motion swaps
 * instantly). Toasts via window.ddToast (cart-toast.js) for transient
 * feedback; inline banner fallback if ddToast is unavailable.
 *
 * ─── State machine ─────────────────────────────────────────────────
 *   INIT
 *     ├─ apiUrl missing                 → FALLBACK_EMBED (Liquid embed)
 *     └─ apiUrl present
 *           ↓
 *   LOADING_INITIAL  (month1 + month2 in parallel)
 *     ├─ success                        → CALENDAR
 *     ├─ 4xx (config)                   → FALLBACK_RUNTIME (re-attach embed)
 *     └─ retry once then fail           → ERROR_LOAD → FALLBACK_RUNTIME
 *
 *   CALENDAR  ← back from TIME
 *     ├─ pick day, cache fresh          → TIME
 *     ├─ pick day, cache stale          → LOADING_DAY → TIME
 *     └─ prev/next month (debounced 200ms)
 *           ↓
 *   TIME      ← back from FORM
 *     ├─ pick slot, start_time > now    → FORM
 *     └─ pick slot, start_time <= now   → toast + refetch day, stay
 *
 *   FORM
 *     └─ submit
 *           ↓
 *   SUBMITTING
 *     ├─ 200                            → CONFIRMED
 *     ├─ 409 (slot gone)                → toast + refetch day → TIME
 *     ├─ 429 (rate limit)               → toast + 10s submit cooldown, stay
 *     ├─ 4xx (validation)               → inline + toast, stay
 *     ├─ 5xx / network                  → toast, stay (ERROR_BOOK overlay)
 *
 * ─── Concurrency control ───────────────────────────────────────────
 *   Month flips debounced 200ms. Each fetchMonth/fetchDay carries a
 *   monotonic requestSeq + AbortController so superseded responses
 *   never overwrite newer state. Slot cache TTL 60s; day refreshed on
 *   TIME enter if stale; slot validated > now() on click (toast +
 *   refetch otherwise). Server-side 409 catches anything missed.
 *
 * ─── Fallback restoration ──────────────────────────────────────────
 *   On mount the original Liquid-rendered .dd-call-booker__glass
 *   (containing the Calendly inline embed + widget.js) is detached
 *   and stashed. FALLBACK_RUNTIME hides the scheduler container and
 *   re-attaches the stashed glass, re-loading Calendly's widget.js
 *   on the fly so the embed hydrates.
 */

/* ──────────────────────────────────────────────────────────────────
 * Swap controller + video player (v5 hero)
 *
 * The hero has a two-panel swap container (video ↔ scheduler). This IIFE:
 *   1. Wires the CTA button to swap from video → scheduler
 *   2. Wires the video play button to inject a Vimeo/YouTube iframe
 *      (URL is pre-computed by Liquid into data-video-embed)
 *
 * Runs independently of the scheduler IIFE below. If either fails the
 * other still works (graceful degradation).
 * ────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window._ddCallBookerSwapInit) return;
  window._ddCallBookerSwapInit = true;

  var swap = document.querySelector('[data-dd-swap]');
  if (!swap) return;

  var motionReduced = false;
  try {
    motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {}

  // ── 1. Video play button ──────────────────────────────────────────
  var videoMedia = swap.querySelector('[data-dd-video]');
  var playBtn = videoMedia && videoMedia.querySelector('.dd-call-booker__video-play');
  if (playBtn && videoMedia) {
    playBtn.addEventListener('click', function () {
      var embedUrl = videoMedia.getAttribute('data-video-embed') || '';
      if (!embedUrl) return;
      if (videoMedia.getAttribute('data-playing') === 'true') return;
      var iframe = document.createElement('iframe');
      iframe.className = 'dd-call-booker__video-iframe';
      iframe.src = embedUrl;
      iframe.title = 'Founder call video';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      iframe.setAttribute('allowfullscreen', '');
      videoMedia.appendChild(iframe);
      videoMedia.setAttribute('data-playing', 'true');
    });
  }

  // ── 2. CTA swap (video → scheduler) ───────────────────────────────
  var triggers = document.querySelectorAll('[data-dd-swap-trigger="scheduler"]');
  if (!triggers.length) return;

  /* Bring the hero swap into view before/while swapping. The hero CTA
     is already near the top so this is a near no-op for it; the Card A
     CTA lives below the fold, so without this the scheduler would
     reveal off-screen and look like nothing happened. Only scrolls
     when the swap is meaningfully out of the viewport. */
  function ensureVisible() {
    try {
      var r = swap.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < 0 || r.top > vh * 0.5) {
        swap.scrollIntoView({ behavior: motionReduced ? 'auto' : 'smooth', block: 'start' });
      }
    } catch (_) {}
  }

  function swapToScheduler() {
    var videoPanel = swap.querySelector('[data-swap-panel="video"]');
    var schedulerPanel = swap.querySelector('[data-swap-panel="scheduler"]');
    if (!videoPanel || !schedulerPanel) return;
    ensureVisible();
    if (schedulerPanel.getAttribute('data-swap-state') === 'active') {
      // Already showing the scheduler — just make sure it's in view
      // (e.g. Card A clicked after a prior hero swap).
      return;
    }

    // Stop any playing video so audio doesn't bleed through during the fade
    var iframe = videoPanel.querySelector('.dd-call-booker__video-iframe');
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    var videoMediaEl = videoPanel.querySelector('[data-dd-video]');
    if (videoMediaEl) videoMediaEl.removeAttribute('data-playing');

    var FADE_MS = motionReduced ? 0 : 320;
    var GRACE_MS = motionReduced ? 0 : 120;

    // Phase 1: fade out video
    videoPanel.setAttribute('data-swap-state', 'fading-out');

    setTimeout(function () {
      // Phase 2: swap which panel is "rendered" — video → hidden, scheduler → fading-in
      videoPanel.setAttribute('data-swap-state', 'hidden');
      videoPanel.setAttribute('inert', '');
      schedulerPanel.setAttribute('data-swap-state', 'fading-in');
      schedulerPanel.removeAttribute('inert');

      // Phase 3: after grace period, trigger fade-in to opaque
      setTimeout(function () {
        // Two rAFs to ensure the browser commits the fading-in starting style
        // before transitioning to active.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            schedulerPanel.setAttribute('data-swap-state', 'active');
            // Move focus into the scheduler for keyboard users
            var focusTarget =
              schedulerPanel.querySelector('[data-autofocus]') ||
              schedulerPanel.querySelector('button:not([disabled]), [tabindex="0"], input:not([disabled])');
            if (focusTarget && typeof focusTarget.focus === 'function') {
              try { focusTarget.focus({ preventScroll: false }); } catch (_) { focusTarget.focus(); }
            }
          });
        });
      }, GRACE_MS);
    }, FADE_MS);
  }

  for (var i = 0; i < triggers.length; i++) {
    triggers[i].addEventListener('click', swapToScheduler);
  }

  // Expose for debugging / programmatic triggering (e.g. analytics handlers)
  window.ddCallBookerSwap = { toScheduler: swapToScheduler };
})();

(function () {
  'use strict';

  // ── singleton guard ───────────────────────────────────────────────
  if (window._ddCallBookerInit) return;
  window._ddCallBookerInit = true;

  // ── config + container discovery ──────────────────────────────────
  var container = document.querySelector('.dd-call-booker__scheduler');
  if (!container) return; // not the welcome page

  var cfg = (typeof window !== 'undefined' && window.DD_CALENDLY_CONFIG) || {};
  var apiUrl = typeof cfg.apiUrl === 'string' ? cfg.apiUrl.replace(/\/+$/, '') : '';

  if (!apiUrl) {
    container.setAttribute('data-state', 'fallback-no-api-url');
    return;
  }

  var motionReduced = false;
  try {
    motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {}

  var TRANSITION_MS = 320;

  // ── DOM helpers ───────────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v == null) continue;
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') {
          node.addEventListener(k.slice(2).toLowerCase(), v);
        } else node.setAttribute(k, String(v));
      }
    }
    if (children) {
      var list = Array.isArray(children) ? children : [children];
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return node;
  }
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }
  function nextFrame() {
    return new Promise(function (r) {
      requestAnimationFrame(function () {
        requestAnimationFrame(r);
      });
    });
  }
  function waitMs(ms) {
    return new Promise(function (r) {
      setTimeout(r, ms);
    });
  }

  // ── UUID + timezone ───────────────────────────────────────────────
  function uuidV4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < bytes.length; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }
  function detectTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_) {
      return 'UTC';
    }
  }

  // ── Date helpers (viewer-local unless suffixed _utc) ──────────────
  function pad2(n) {
    return String(n).padStart(2, '0');
  }
  function utcYMD(date) {
    return date.getUTCFullYear() + '-' + pad2(date.getUTCMonth() + 1) + '-' + pad2(date.getUTCDate());
  }
  function localYMD(date, tz) {
    // YYYY-MM-DD in viewer's timezone (en-CA locale gives ISO format)
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
    }).format(date);
  }
  function formatTime(date, tz) {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: tz,
    }).format(date);
  }
  function formatLongDate(date) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    }).format(date);
  }
  function formatLongDateTime(date, tz) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: tz,
    }).format(date);
  }
  function formatMonthYear(year, month) {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
      .format(new Date(year, month, 15));
  }
  function formatMonthDay(date) {
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(date);
  }
  function shortTimezoneLabel(tz) {
    // Best-effort short label: en-US `timeZoneName: short` gives "EST" / "PST"
    try {
      var s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).format(new Date());
      var m = s.match(/[A-Z]{2,5}$/);
      return m ? m[0] : tz.replace(/_/g, ' ');
    } catch (_) {
      return tz.replace(/_/g, ' ');
    }
  }
  function dateFromYMD(ymd) {
    var p = ymd.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  // ── component ─────────────────────────────────────────────────────
  function Booker(root) {
    this.root = root;
    this.customerName = root.getAttribute('data-customer-name') || '';
    this.customerEmail = root.getAttribute('data-customer-email') || '';
    this.fallbackLink = root.getAttribute('data-fallback-link') || '';
    this.fallbackEmbedUrl = root.getAttribute('data-calendly-url') || '';
    this.timezone = detectTimezone();

    // Cursor + selections
    var now = new Date();
    this.currentMonth = { year: now.getFullYear(), month: now.getMonth() };
    this.selectedDay = null;     // 'YYYY-MM-DD' viewer-local
    this.selectedSlot = null;

    // State
    this.state = 'INIT';
    this.lastError = null;
    this.confirmation = null;
    this.idemKey = null;
    this.submitCooldownUntil = 0;

    // Caches + concurrency
    this.slotsByMonth = {};      // 'YYYY-MM' -> { days: { 'YYYY-MM-DD': [...] }, loadedAt }
    this.requestSeqMonth = {};
    this.requestSeqDay = {};
    this.abortMonth = {};
    this.abortDay = {};
    this.monthFlipTimer = null;
    this.transitionLock = false;
    this.bannerTimer = null;

    // Fallback DOM stash
    this.fallbackGlass = null;

    // DOM refs (set in mount)
    this.shell = null;
    this.bannerHost = null;
    this.viewport = null;
  }

  // ── mount ────────────────────────────────────────────────────────
  Booker.prototype.mount = function () {
    // Detach the original Liquid-rendered glass (with Calendly embed) for
    // potential FALLBACK_RUNTIME re-attachment. We do this before clearing
    // the container so the embed's <script> tag (already in DOM) doesn't
    // get garbage-collected and we can re-attach it whole.
    var existingGlass = this.root.querySelector('.dd-call-booker__glass');
    if (existingGlass) {
      this.fallbackGlass = existingGlass;
      existingGlass.parentNode.removeChild(existingGlass);
    }

    clear(this.root);
    this.root.setAttribute('data-state', 'loading');

    this.shell = el('div', { class: 'dd-call-booker__glass dd-call-booker__glass--scheduler' });
    this.bannerHost = el('div', { class: 'dd-sched__banner-host', 'aria-live': 'polite', 'aria-atomic': 'true' });
    this.viewport = el('div', { class: 'dd-sched__viewport' });
    this.shell.appendChild(this.bannerHost);
    this.shell.appendChild(this.viewport);
    this.root.appendChild(this.shell);

    // Initial loading view (no transition — first paint)
    var loading = this.renderLoading();
    loading.classList.add('dd-sched__view');
    this.viewport.appendChild(loading);

    this.bindGlobalKeys();
    this.loadInitial();
  };

  // ── ESC = back ───────────────────────────────────────────────────
  Booker.prototype.bindGlobalKeys = function () {
    var self = this;
    self.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.transitionLock) return;
      if (self.state === 'TIME') {
        e.preventDefault();
        self.backToCalendar();
      } else if (self.state === 'FORM') {
        e.preventDefault();
        self.backToTime();
      }
    });
  };

  // ── initial load (current month + next month in parallel) ────────
  Booker.prototype.loadInitial = function () {
    var self = this;
    self.state = 'LOADING_INITIAL';
    self.root.setAttribute('data-state', 'loading');

    var nextCursor = self.shiftMonth(self.currentMonth, 1);

    Promise.allSettled([
      self.fetchMonth(self.currentMonth.year, self.currentMonth.month),
      self.fetchMonth(nextCursor.year, nextCursor.month),
    ]).then(function (results) {
      var m1 = results[0];
      if (m1.status === 'rejected') {
        var err = m1.reason || {};
        if (typeof err.status === 'number' && err.status >= 400 && err.status < 500) {
          self.activateFallback('slots-4xx-config');
          return;
        }
        // Retry once for transient failures
        self.fetchMonth(self.currentMonth.year, self.currentMonth.month).then(function () {
          self.enterCalendar();
        }).catch(function () {
          self.state = 'ERROR_LOAD';
          self.swapView(self.renderErrorLoad(), 'forward');
          self.emitToast("Calendly is having issues. Try again.", 'error', 6000);
          setTimeout(function () {
            if (self.state === 'ERROR_LOAD') self.activateFallback('slots-failed-twice');
          }, 6000);
        });
        return;
      }
      self.enterCalendar();
    });
  };

  Booker.prototype.enterCalendar = function () {
    this.state = 'CALENDAR';
    this.root.setAttribute('data-state', 'calendar');
    this.swapView(this.renderCalendar(), 'forward');
  };

  // ── FALLBACK_RUNTIME: re-attach detached embed ───────────────────
  Booker.prototype.activateFallback = function (reason) {
    console.warn('[dd-call-booker] FALLBACK_RUNTIME:', reason);
    this.state = 'FALLBACK_RUNTIME';
    this.root.setAttribute('data-state', 'fallback-runtime');

    if (!this.fallbackGlass) {
      // No embed to restore — leave scheduler showing ERROR_LOAD with retry
      return;
    }

    // Tear down the custom scheduler shell and put the embed glass back
    // in its original parent (this.root === .dd-call-booker__scheduler).
    clear(this.root);
    this.root.appendChild(this.fallbackGlass);

    // Trigger Calendly widget hydration. If widget.js already loaded
    // and exposed window.Calendly, call initInlineWidget directly. Else
    // (re)inject the script — the auto-poller picks up our re-attached
    // .calendly-inline-widget element.
    var inlineDiv = this.fallbackGlass.querySelector('.calendly-inline-widget');
    if (window.Calendly && typeof window.Calendly.initInlineWidget === 'function' && inlineDiv && this.fallbackEmbedUrl) {
      try {
        window.Calendly.initInlineWidget({ url: this.fallbackEmbedUrl, parentElement: inlineDiv });
      } catch (e) {
        console.error('[dd-call-booker] Calendly init failed', e);
      }
    } else {
      var existingScript = document.querySelector('script[data-dd-calendly-widget]');
      if (!existingScript) {
        var s = document.createElement('script');
        s.src = 'https://assets.calendly.com/assets/external/widget.js';
        s.async = true;
        s.setAttribute('data-dd-calendly-widget', '1');
        document.head.appendChild(s);
      }
    }
  };

  // ── Month / day arithmetic ───────────────────────────────────────
  Booker.prototype.monthKey = function (year, month) {
    return year + '-' + pad2(month + 1);
  };
  Booker.prototype.shiftMonth = function (cursor, delta) {
    var d = new Date(cursor.year, cursor.month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  };
  Booker.prototype.daysInMonth = function (year, month) {
    return new Date(year, month + 1, 0).getDate();
  };
  Booker.prototype.firstWeekday = function (year, month) {
    return new Date(year, month, 1).getDay();
  };

  // ── fetch a month's slots (request-token + abort-safe) ──────────
  Booker.prototype.fetchMonth = function (year, month) {
    var self = this;
    var key = self.monthKey(year, month);

    // Fresh cache hit → resolve immediately
    var cached = self.slotsByMonth[key];
    if (cached && Date.now() - cached.loadedAt < 60000) {
      return Promise.resolve(cached);
    }

    var seq = (self.requestSeqMonth[key] || 0) + 1;
    self.requestSeqMonth[key] = seq;
    if (self.abortMonth[key]) {
      try { self.abortMonth[key].abort(); } catch (_) {}
    }
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    self.abortMonth[key] = ac;

    var firstDay = new Date(year, month, 1);
    var lastDay = new Date(year, month, self.daysInMonth(year, month));
    var now = new Date();
    var startCursor = firstDay > now ? firstDay : now;

    // Skip months entirely in the past
    if (lastDay < now) {
      var empty = { days: {}, loadedAt: Date.now() };
      self.slotsByMonth[key] = empty;
      return Promise.resolve(empty);
    }

    // Split into 7-day windows (proxy max)
    var windows = [];
    var w = startCursor;
    while (w <= lastDay) {
      var end = new Date(w.getTime() + 6 * 86400000);
      if (end > lastDay) end = lastDay;
      windows.push({ start: utcYMD(w), end: utcYMD(end) });
      w = new Date(end.getTime() + 86400000);
    }

    var init = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (ac) init.signal = ac.signal;

    var fetches = windows.map(function (win) {
      var url = apiUrl + '/api/calendly/slots?start_date=' + win.start + '&end_date=' + win.end;
      return fetch(url, init).then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            var err = new Error((body.error && body.error.message) || 'slots failed');
            err.status = res.status;
            err.code = body.error && body.error.code;
            throw err;
          });
        }
        return res.json().then(function (body) { return (body && body.slots) || []; });
      });
    });

    return Promise.all(fetches).then(function (lists) {
      // Bail if superseded
      if (seq !== self.requestSeqMonth[key]) {
        return { aborted: true };
      }
      var byDay = {};
      var all = [];
      lists.forEach(function (l) { all = all.concat(l); });
      all.forEach(function (s) {
        var d = new Date(s.start_time);
        var dayKey = localYMD(d, self.timezone);
        if (!byDay[dayKey]) byDay[dayKey] = [];
        byDay[dayKey].push(s);
      });
      Object.keys(byDay).forEach(function (k) {
        byDay[k].sort(function (a, b) { return new Date(a.start_time) - new Date(b.start_time); });
      });
      var data = { days: byDay, loadedAt: Date.now() };
      self.slotsByMonth[key] = data;
      return data;
    });
  };

  // ── fetch a single day (re-uses month-window endpoint) ──────────
  Booker.prototype.fetchDay = function (yyyymmdd) {
    var self = this;
    var seq = (self.requestSeqDay[yyyymmdd] || 0) + 1;
    self.requestSeqDay[yyyymmdd] = seq;
    if (self.abortDay[yyyymmdd]) {
      try { self.abortDay[yyyymmdd].abort(); } catch (_) {}
    }
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    self.abortDay[yyyymmdd] = ac;

    var init = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (ac) init.signal = ac.signal;

    // Use yyyymmdd as both start & end (UTC interpretation by proxy);
    // we filter to the viewer-local day client-side after fetch.
    var url = apiUrl + '/api/calendly/slots?start_date=' + yyyymmdd + '&end_date=' + yyyymmdd;
    return fetch(url, init).then(function (res) {
      if (!res.ok) {
        var e = new Error('day fetch failed');
        e.status = res.status;
        throw e;
      }
      return res.json();
    }).then(function (body) {
      if (seq !== self.requestSeqDay[yyyymmdd]) return null;
      var raw = (body && body.slots) || [];
      // Re-bucket to viewer-local YYYY-MM-DD
      var matched = raw.filter(function (s) {
        return localYMD(new Date(s.start_time), self.timezone) === yyyymmdd;
      });
      matched.sort(function (a, b) {
        return new Date(a.start_time) - new Date(b.start_time);
      });
      // Merge into month cache
      var parts = yyyymmdd.split('-');
      var mKey = parts[0] + '-' + parts[1];
      if (self.slotsByMonth[mKey]) {
        self.slotsByMonth[mKey].days[yyyymmdd] = matched;
        // Don't bump loadedAt — month cache is wider; just freshen this day
      }
      return matched;
    });
  };

  // ── view swap (forward = slide L→R off, R→L on) ──────────────────
  Booker.prototype.swapView = function (newView, direction) {
    var self = this;
    if (self.transitionLock) {
      // Coalesce — last swap wins. Replace inert flag handling so we don't deadlock.
    }
    newView.classList.add('dd-sched__view');

    if (motionReduced) {
      clear(self.viewport);
      self.viewport.appendChild(newView);
      self.focusFirstInteractive(newView);
      return Promise.resolve();
    }

    var oldView = self.viewport.querySelector('.dd-sched__view');
    if (!oldView) {
      self.viewport.appendChild(newView);
      self.focusFirstInteractive(newView);
      return Promise.resolve();
    }

    self.transitionLock = true;
    self.viewport.setAttribute('inert', '');

    var enterClass = direction === 'back' ? 'dd-sched__view--enter-left' : 'dd-sched__view--enter-right';
    var exitClass = direction === 'back' ? 'dd-sched__view--exit-right' : 'dd-sched__view--exit-left';

    newView.classList.add(enterClass);
    self.viewport.appendChild(newView);

    return nextFrame().then(function () {
      newView.classList.remove(enterClass);
      oldView.classList.add(exitClass);
      return waitMs(TRANSITION_MS + 60);
    }).then(function () {
      if (oldView.parentNode === self.viewport) self.viewport.removeChild(oldView);
      self.viewport.removeAttribute('inert');
      self.transitionLock = false;
      self.focusFirstInteractive(newView);
    }).catch(function (err) {
      console.error('[dd-call-booker] swapView failed', err);
      self.viewport.removeAttribute('inert');
      self.transitionLock = false;
    });
  };

  // ── replace current view in-place (no animation, same step) ─────
  Booker.prototype.replaceView = function (newView) {
    var self = this;
    newView.classList.add('dd-sched__view');
    var old = self.viewport.querySelector('.dd-sched__view');
    if (old) self.viewport.replaceChild(newView, old);
    else self.viewport.appendChild(newView);
    self.focusFirstInteractive(newView);
  };

  Booker.prototype.focusFirstInteractive = function (view) {
    if (!view) return;
    var target = view.querySelector('[data-autofocus]') ||
                 view.querySelector('button:not([disabled]), input:not([disabled]), [tabindex="0"]');
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (_) {}
    }
  };

  // ── toast (ddToast preferred, inline banner fallback) ───────────
  Booker.prototype.emitToast = function (msg, type, duration) {
    type = type || 'info';
    duration = duration || 4500;
    if (typeof window.ddToast === 'function') {
      try {
        window.ddToast(msg, { type: type, duration: duration });
        return;
      } catch (_) {}
    }
    this.showInlineBanner(msg, type, duration);
  };

  Booker.prototype.showInlineBanner = function (msg, type, duration) {
    var self = this;
    if (self.bannerTimer) {
      clearTimeout(self.bannerTimer);
      self.bannerTimer = null;
    }
    clear(self.bannerHost);
    var banner = el('div', {
      class: 'dd-sched__banner dd-sched__banner--' + type,
      role: type === 'error' ? 'alert' : 'status',
    }, [
      el('span', { class: 'dd-sched__banner-msg', text: msg }),
      el('button', {
        class: 'dd-sched__banner-close',
        type: 'button',
        'aria-label': 'Dismiss',
        onclick: function () {
          clear(self.bannerHost);
          if (self.bannerTimer) { clearTimeout(self.bannerTimer); self.bannerTimer = null; }
        },
      }, '\u00d7'),
    ]);
    self.bannerHost.appendChild(banner);
    self.bannerTimer = setTimeout(function () {
      clear(self.bannerHost);
      self.bannerTimer = null;
    }, duration);
  };

  // ── LOADING (initial + retry) ────────────────────────────────────
  Booker.prototype.renderLoading = function () {
    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--loading',
      'aria-live': 'polite', 'aria-busy': 'true',
    }, [
      el('div', { class: 'dd-sched__spinner', 'aria-hidden': 'true' }),
      el('p', { class: 'dd-sched__loading-text', text: 'Loading available times\u2026' }),
    ]);
  };

  // ── CALENDAR ─────────────────────────────────────────────────────
  Booker.prototype.renderCalendar = function () {
    var self = this;
    var year = self.currentMonth.year;
    var month = self.currentMonth.month;
    var key = self.monthKey(year, month);
    var monthData = self.slotsByMonth[key];
    var availableDays = (monthData && monthData.days) || {};

    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--calendar' });

    view.appendChild(el('p', {
      class: 'dd-sched__step-indicator',
      text: 'Step 1 of 3 \u2014 Pick a day',
    }));

    // Header with prev/next nav
    var now = new Date();
    var thisMonthFirst = new Date(now.getFullYear(), now.getMonth(), 1);
    var displayedFirst = new Date(year, month, 1);
    var prevDisabled = displayedFirst <= thisMonthFirst;
    // No hard cap on next — backend covers what it covers; user can flip forward freely
    // but we soft-cap at 6 months ahead to prevent infinite scrolling.
    var sixMonthsOut = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    var nextDisabled = displayedFirst >= sixMonthsOut;

    var header = el('div', { class: 'dd-sched__cal-header' }, [
      el('button', {
        class: 'dd-sched__cal-nav dd-sched__cal-nav--prev',
        type: 'button',
        'aria-label': 'Previous month',
        disabled: prevDisabled ? 'disabled' : null,
        onclick: prevDisabled ? null : function () { self.flipMonth(-1); },
      }, '\u2039'),
      el('h3', {
        class: 'dd-sched__cal-month',
        text: formatMonthYear(year, month),
        'aria-live': 'polite',
      }),
      el('button', {
        class: 'dd-sched__cal-nav dd-sched__cal-nav--next',
        type: 'button',
        'aria-label': 'Next month',
        'data-autofocus': '',
        disabled: nextDisabled ? 'disabled' : null,
        onclick: nextDisabled ? null : function () { self.flipMonth(1); },
      }, '\u203A'),
    ]);
    view.appendChild(header);

    // Day-of-week header
    var dowHeader = el('div', { class: 'dd-sched__cal-dow', role: 'row' });
    var dowShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    var dowFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    for (var d = 0; d < 7; d++) {
      dowHeader.appendChild(el('div', {
        class: 'dd-sched__cal-dow-cell',
        role: 'columnheader',
        'aria-label': dowFull[d],
        text: dowShort[d],
      }));
    }
    view.appendChild(dowHeader);

    // Calendar grid
    var grid = el('div', { class: 'dd-sched__cal-grid', role: 'grid', 'aria-label': formatMonthYear(year, month) });
    var firstW = self.firstWeekday(year, month);
    var daysCount = self.daysInMonth(year, month);
    var totalCells = Math.ceil((firstW + daysCount) / 7) * 7;
    var todayKey = localYMD(now, self.timezone);
    var availableCount = 0;

    for (var i = 0; i < totalCells; i++) {
      var dayNum = i - firstW + 1;
      var inMonth = dayNum >= 1 && dayNum <= daysCount;

      if (!inMonth) {
        grid.appendChild(el('div', {
          class: 'dd-sched__cal-cell dd-sched__cal-cell--blank',
          role: 'gridcell',
          'aria-hidden': 'true',
        }));
        continue;
      }

      var cellDate = new Date(year, month, dayNum);
      var cellKey = localYMD(cellDate, self.timezone);
      var slotsForCell = availableDays[cellKey] || [];
      var hasAvail = slotsForCell.length > 0;
      if (hasAvail) availableCount++;
      var isToday = cellKey === todayKey;
      var isSelected = self.selectedDay === cellKey;
      var endOfCellDay = new Date(year, month, dayNum, 23, 59, 59);
      var isPast = endOfCellDay < now;
      var disabled = isPast || !hasAvail;

      var aria = formatMonthDay(cellDate);
      aria += hasAvail
        ? ', ' + slotsForCell.length + ' time' + (slotsForCell.length === 1 ? '' : 's') + ' available'
        : (isPast ? ', past' : ', no availability');

      var cellClass = 'dd-sched__cal-cell';
      if (isToday) cellClass += ' dd-sched__cal-cell--today';
      if (isSelected) cellClass += ' dd-sched__cal-cell--selected';
      if (isPast) cellClass += ' dd-sched__cal-cell--past';
      if (hasAvail && !isPast) cellClass += ' dd-sched__cal-cell--available';
      if (disabled) cellClass += ' dd-sched__cal-cell--disabled';

      var attrs = {
        class: cellClass,
        type: 'button',
        role: 'gridcell',
        'aria-label': aria,
        'aria-disabled': disabled ? 'true' : null,
        'aria-current': isToday ? 'date' : null,
        'aria-selected': isSelected ? 'true' : null,
        disabled: disabled ? 'disabled' : null,
      };
      if (!disabled) {
        (function (dayKey) {
          attrs.onclick = function () { self.pickDay(dayKey); };
        })(cellKey);
      }

      grid.appendChild(el('button', attrs, [
        el('span', { class: 'dd-sched__cal-cell-num', text: String(dayNum) }),
        hasAvail && !isPast ? el('span', { class: 'dd-sched__cal-cell-dot', 'aria-hidden': 'true' }) : null,
      ]));
    }
    view.appendChild(grid);

    // Empty-month banner
    if (availableCount === 0 && monthData) {
      view.appendChild(el('p', {
        class: 'dd-sched__cal-empty',
        text: 'No availability this month \u2014 try the next month \u203A',
      }));
    }

    view.appendChild(el('p', {
      class: 'dd-sched__cal-footer',
      text: '30 min with Kristi Day \u00b7 Times in ' + shortTimezoneLabel(self.timezone),
    }));

    return view;
  };

  // ── month flip (debounced, request-token guarded) ───────────────
  Booker.prototype.flipMonth = function (delta) {
    var self = this;
    if (self.monthFlipTimer) clearTimeout(self.monthFlipTimer);
    // Optimistically shift cursor so rapid clicks accumulate
    self.currentMonth = self.shiftMonth(self.currentMonth, delta);
    self.monthFlipTimer = setTimeout(function () {
      self.commitMonthFlip(delta > 0 ? 'forward' : 'back');
    }, 180);
  };

  Booker.prototype.commitMonthFlip = function (direction) {
    var self = this;
    var year = self.currentMonth.year;
    var month = self.currentMonth.month;
    var key = self.monthKey(year, month);

    var cached = self.slotsByMonth[key];
    var fresh = cached && Date.now() - cached.loadedAt < 60000;

    // Always swap visually first (with the data we have)
    self.swapView(self.renderCalendar(), direction);

    if (!fresh) {
      self.fetchMonth(year, month).then(function (data) {
        if (!data || data.aborted) return;
        if (self.state !== 'CALENDAR') return;
        // Only re-render if we're still on this month
        if (self.monthKey(self.currentMonth.year, self.currentMonth.month) !== key) return;
        self.replaceView(self.renderCalendar());
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.error('[dd-call-booker] month fetch failed', err);
        self.emitToast("Couldn't load that month. Try another.", 'error');
      });
    }
  };

  // ── day pick → TIME ─────────────────────────────────────────────
  Booker.prototype.pickDay = function (dayKey) {
    var self = this;
    if (self.transitionLock) return;
    self.selectedDay = dayKey;
    var monthData = self.slotsByMonth[self.monthKey(self.currentMonth.year, self.currentMonth.month)];
    var stale = !monthData || Date.now() - monthData.loadedAt > 60000;

    if (stale) {
      // Show TIME view with loading state, refetch in background, then re-render
      self.state = 'TIME';
      self.root.setAttribute('data-state', 'time');
      self.swapView(self.renderTimeLoading(dayKey), 'forward');
      self.fetchDay(dayKey).then(function (slots) {
        if (slots === null) return; // superseded
        if (self.state === 'TIME' && self.selectedDay === dayKey) {
          self.replaceView(self.renderTime(dayKey));
        }
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.emitToast("Couldn't load times for that day. Try another.", 'error');
        self.backToCalendar();
      });
      return;
    }

    self.state = 'TIME';
    self.root.setAttribute('data-state', 'time');
    self.swapView(self.renderTime(dayKey), 'forward');
  };

  // ── TIME ─────────────────────────────────────────────────────────
  Booker.prototype.renderTime = function (dayKey) {
    var self = this;
    var monthData = self.slotsByMonth[self.monthKey(self.currentMonth.year, self.currentMonth.month)];
    var slots = (monthData && monthData.days[dayKey]) || [];
    // Filter out any that have slipped into the past mid-browse
    var now = Date.now();
    slots = slots.filter(function (s) { return new Date(s.start_time).getTime() > now; });
    var dayDate = dateFromYMD(dayKey);

    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--time' });

    view.appendChild(self.renderStepHeader('calendar', 'Step 2 of 3 \u2014 Pick a time'));

    view.appendChild(el('h3', {
      class: 'dd-sched__time-day',
      text: formatLongDate(dayDate),
    }));

    if (slots.length === 0) {
      view.appendChild(el('p', {
        class: 'dd-sched__time-empty',
        text: 'No times left for this day. Pick another day.',
      }));
      return view;
    }

    var grid = el('div', { class: 'dd-sched__time-grid', role: 'list' });
    slots.forEach(function (slot) {
      var d = new Date(slot.start_time);
      var label = formatTime(d, self.timezone);
      var aria = label + ' on ' + formatLongDate(dayDate);
      grid.appendChild(el('button', {
        class: 'dd-sched__time-slot',
        type: 'button',
        role: 'listitem',
        'data-start': slot.start_time,
        'aria-label': aria,
        onclick: function () { self.pickSlot(slot); },
      }, label));
    });
    view.appendChild(grid);

    view.appendChild(el('p', {
      class: 'dd-sched__time-tz',
      text: 'All times in ' + shortTimezoneLabel(self.timezone),
    }));

    return view;
  };

  Booker.prototype.renderTimeLoading = function (dayKey) {
    var self = this;
    var dayDate = dateFromYMD(dayKey);
    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--time' });
    view.appendChild(self.renderStepHeader('calendar', 'Step 2 of 3 \u2014 Pick a time'));
    view.appendChild(el('h3', {
      class: 'dd-sched__time-day',
      text: formatLongDate(dayDate),
    }));
    view.appendChild(el('div', { class: 'dd-sched__spinner', 'aria-hidden': 'true' }));
    view.appendChild(el('p', { class: 'dd-sched__time-loading', text: 'Loading times\u2026' }));
    return view;
  };

  Booker.prototype.backToCalendar = function () {
    if (this.transitionLock) return;
    this.state = 'CALENDAR';
    this.root.setAttribute('data-state', 'calendar');
    this.swapView(this.renderCalendar(), 'back');
  };

  Booker.prototype.backToTime = function () {
    if (this.transitionLock) return;
    this.state = 'TIME';
    this.root.setAttribute('data-state', 'time');
    this.swapView(this.renderTime(this.selectedDay), 'back');
  };

  // ── slot pick → FORM (with past-slot guard) ─────────────────────
  Booker.prototype.pickSlot = function (slot) {
    var self = this;
    if (self.transitionLock) return;
    var slotStart = new Date(slot.start_time).getTime();
    if (slotStart <= Date.now()) {
      self.emitToast('That time has passed. Pick another.', 'warn');
      self.fetchDay(self.selectedDay).then(function (slots) {
        if (slots === null) return;
        if (self.state === 'TIME') self.replaceView(self.renderTime(self.selectedDay));
      });
      return;
    }
    self.selectedSlot = slot;
    self.idemKey = uuidV4();
    self.lastError = null;
    self.state = 'FORM';
    self.root.setAttribute('data-state', 'form');
    self.swapView(self.renderForm(false), 'forward');
  };

  // ── shared step header (back + step indicator) ──────────────────
  Booker.prototype.renderStepHeader = function (backTarget, stepText) {
    var self = this;
    var backLabel = backTarget === 'calendar' ? 'Back to calendar' : 'Back to time picker';
    return el('div', { class: 'dd-sched__step-header' }, [
      el('button', {
        class: 'dd-sched__back',
        type: 'button',
        'data-autofocus': '',
        'aria-label': backLabel,
        onclick: function () {
          if (backTarget === 'calendar') self.backToCalendar();
          else self.backToTime();
        },
      }, [
        el('span', { class: 'dd-sched__back-icon', 'aria-hidden': 'true', text: '\u2190' }),
        el('span', { class: 'dd-sched__back-label', text: 'Back' }),
      ]),
      el('p', { class: 'dd-sched__step-indicator', text: stepText }),
    ]);
  };

  // ── FORM ─────────────────────────────────────────────────────────
  Booker.prototype.renderForm = function (submitting) {
    var self = this;
    var slot = self.selectedSlot;
    var slotDate = slot ? new Date(slot.start_time) : null;

    var nameInput, emailInput;

    function rerender() {
      self.replaceView(self.renderForm(false));
    }

    function submit(ev) {
      ev.preventDefault();
      if (Date.now() < self.submitCooldownUntil) {
        self.emitToast('Wait a moment before retrying.', 'warn');
        return;
      }
      var name = (nameInput.value || '').trim();
      var email = (emailInput.value || '').trim();
      if (name.length < 2) {
        self.lastError = 'Please enter your full name.';
        rerender();
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        self.lastError = 'Please enter a valid email address.';
        rerender();
        return;
      }
      self.submitBooking({ name: name, email: email });
    }

    var form = el('form', {
      class: 'dd-sched__view-inner dd-sched__view-inner--form',
      novalidate: 'novalidate',
      onsubmit: submit,
    });

    // Step header — Back disabled during submit
    var stepHeader = el('div', { class: 'dd-sched__step-header' }, [
      el('button', {
        class: 'dd-sched__back',
        type: 'button',
        disabled: submitting ? 'disabled' : null,
        'aria-label': 'Back to time picker',
        onclick: function () {
          if (submitting) return;
          self.backToTime();
        },
      }, [
        el('span', { class: 'dd-sched__back-icon', 'aria-hidden': 'true', text: '\u2190' }),
        el('span', { class: 'dd-sched__back-label', text: 'Back' }),
      ]),
      el('p', { class: 'dd-sched__step-indicator', text: 'Step 3 of 3 \u2014 Confirm' }),
    ]);
    form.appendChild(stepHeader);

    form.appendChild(el('div', { class: 'dd-sched__form-summary' }, [
      el('span', { class: 'dd-sched__form-summary-label', text: 'Your time' }),
      el('span', {
        class: 'dd-sched__form-summary-value',
        text: slotDate ? formatLongDateTime(slotDate, self.timezone) : '',
      }),
    ]));

    if (self.lastError) {
      form.appendChild(el('div', {
        class: 'dd-sched__form-error',
        role: 'alert',
        text: self.lastError,
      }));
    }

    var nameLabel = el('label', { class: 'dd-sched__field' }, [
      el('span', { class: 'dd-sched__field-label', text: 'Your name' }),
    ]);
    nameInput = el('input', {
      class: 'dd-sched__field-input',
      type: 'text',
      name: 'name',
      autocomplete: 'name',
      required: 'required',
      value: self.customerName,
      placeholder: 'Sarah Patel',
      disabled: submitting ? 'disabled' : null,
      oninput: function (e) { self.customerName = e.target.value; },
    });
    nameLabel.appendChild(nameInput);
    form.appendChild(nameLabel);

    var emailLabel = el('label', { class: 'dd-sched__field' }, [
      el('span', { class: 'dd-sched__field-label', text: 'Email' }),
    ]);
    emailInput = el('input', {
      class: 'dd-sched__field-input',
      type: 'email',
      name: 'email',
      autocomplete: 'email',
      required: 'required',
      value: self.customerEmail,
      placeholder: 'you@example.com',
      disabled: submitting ? 'disabled' : null,
      oninput: function (e) { self.customerEmail = e.target.value; },
    });
    emailLabel.appendChild(emailInput);
    form.appendChild(emailLabel);

    form.appendChild(el('button', {
      class: 'dd-sched__submit',
      type: 'submit',
      disabled: submitting ? 'disabled' : null,
      'aria-busy': submitting ? 'true' : null,
    }, submitting ? 'Confirming\u2026' : 'Confirm booking'));

    form.appendChild(el('p', {
      class: 'dd-sched__form-meta',
      text: "We'll send a calendar invite to your email.",
    }));

    return form;
  };

  // ── SUBMIT → CONFIRMED / error ───────────────────────────────────
  Booker.prototype.submitBooking = function (formData) {
    var self = this;
    if (!self.selectedSlot) return;
    if (!self.idemKey) self.idemKey = uuidV4();

    self.state = 'SUBMITTING';
    self.root.setAttribute('data-state', 'submitting');
    // Replace in place — submit state is just FORM with disabled inputs
    self.replaceView(self.renderForm(true));

    fetch(apiUrl + '/api/calendly/book', {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Idempotency-Key': self.idemKey,
      },
      body: JSON.stringify({
        start_time: self.selectedSlot.start_time,
        name: formData.name,
        email: formData.email,
        timezone: self.timezone,
      }),
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (body) {
        return { res: res, body: body };
      });
    }).then(function (out) {
      // 200: success
      if (out.res.ok) {
        self.confirmation = (out.body && out.body.booking) || null;
        self.state = 'CONFIRMED';
        self.root.setAttribute('data-state', 'confirmed');
        self.swapView(self.renderConfirmed(), 'forward');
        try {
          (window.dataLayer = window.dataLayer || []).push({
            event: 'welcome_booking_confirmed',
            booking_uri: self.confirmation && self.confirmation.uri,
          });
        } catch (_) {}
        return;
      }
      // 409: slot taken
      if (out.res.status === 409) {
        self.emitToast('That time was just booked. Pick another.', 'warn', 5000);
        self.idemKey = null;
        self.selectedSlot = null;
        self.state = 'TIME';
        self.root.setAttribute('data-state', 'time');
        // Refetch then slide back
        self.fetchDay(self.selectedDay).finally(function () {
          self.swapView(self.renderTime(self.selectedDay), 'back');
        });
        return;
      }
      // 429: rate limit
      if (out.res.status === 429) {
        self.submitCooldownUntil = Date.now() + 10000;
        self.emitToast('Too many requests. Wait 10 seconds.', 'warn', 6000);
        self.idemKey = uuidV4();
        self.state = 'FORM';
        self.root.setAttribute('data-state', 'form');
        self.replaceView(self.renderForm(false));
        return;
      }
      // 4xx: validation
      if (out.res.status >= 400 && out.res.status < 500) {
        var msg = (out.body && out.body.error && out.body.error.message)
                  || 'Check the form fields and try again.';
        self.lastError = msg;
        self.emitToast(msg, 'error', 5000);
        self.idemKey = uuidV4();
        self.state = 'FORM';
        self.root.setAttribute('data-state', 'form');
        self.replaceView(self.renderForm(false));
        return;
      }
      // 5xx
      self.lastError = 'Something went wrong on our end. Try the same slot again.';
      self.emitToast('Booking failed. Try again.', 'error', 5000);
      self.state = 'FORM';
      self.root.setAttribute('data-state', 'form');
      self.replaceView(self.renderForm(false));
    }).catch(function (err) {
      console.error('[dd-call-booker] book failed', err);
      self.lastError = "We couldn't reach our scheduler. Check your connection and try again.";
      self.emitToast('Connection issue. Try again.', 'error', 5000);
      self.state = 'FORM';
      self.root.setAttribute('data-state', 'form');
      self.replaceView(self.renderForm(false));
    });
  };

  // ── CONFIRMED ────────────────────────────────────────────────────
  Booker.prototype.renderConfirmed = function () {
    var self = this;
    var c = self.confirmation || {};
    var startDate = c.start_time ? new Date(c.start_time) : null;

    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--confirmed',
      role: 'status', 'aria-live': 'polite',
    }, [
      el('div', { class: 'dd-sched__confirmed-icon', 'aria-hidden': 'true', text: '\u2713' }),
      el('h3', { class: 'dd-sched__confirmed-title', text: "You're booked." }),
      el('p', {
        class: 'dd-sched__confirmed-time',
        text: startDate ? formatLongDateTime(startDate, self.timezone) : '',
      }),
      el('p', {
        class: 'dd-sched__confirmed-detail',
        text: 'Calendar invite sent to ' + (c.email || 'your email') + '. Look for it in the next few minutes.',
      }),
      c.join_url ? el('p', { class: 'dd-sched__confirmed-link' }, [
        el('span', { text: 'Zoom link: ' }),
        el('a', { href: c.join_url, target: '_blank', rel: 'noopener', text: c.join_url }),
      ]) : null,
      el('div', { class: 'dd-sched__confirmed-actions' }, [
        c.reschedule_url ? el('a', {
          class: 'dd-sched__confirmed-action',
          href: c.reschedule_url, target: '_blank', rel: 'noopener', text: 'Reschedule',
        }) : null,
        c.cancel_url ? el('a', {
          class: 'dd-sched__confirmed-action dd-sched__confirmed-action--ghost',
          href: c.cancel_url, target: '_blank', rel: 'noopener', text: 'Cancel',
        }) : null,
      ]),
    ]);
  };

  // ── ERROR_LOAD ───────────────────────────────────────────────────
  Booker.prototype.renderErrorLoad = function () {
    var self = this;
    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--error',
      role: 'alert',
    }, [
      el('p', { class: 'dd-sched__error-title', text: "We can't load times right now." }),
      el('p', {
        class: 'dd-sched__error-body',
        text: 'Trying again in a moment. The Calendly link below still works.',
      }),
      el('button', {
        class: 'dd-sched__error-retry',
        type: 'button',
        'data-autofocus': '',
        onclick: function () {
          self.state = 'LOADING_INITIAL';
          self.swapView(self.renderLoading(), 'forward');
          self.loadInitial();
        },
      }, 'Retry'),
    ]);
  };

  // ── boot ─────────────────────────────────────────────────────────
  try {
    new Booker(container).mount();
  } catch (err) {
    console.error('[dd-call-booker] mount failed', err);
  }
})();
